import { AuthorizationType } from "@nova/db-table-integration-authorizations"
import { IntegrationType } from "@nova/schemas"
import type { IntegrationAuthorizationResponse } from "@nova/data-facade"
import { FunctionComponent, useState } from "react"
import { TAKMission, useTAKMissions } from "./useTAKMissions"
import { Spinner } from "themes/default-tailwind"
import { useUpdateIntegrationAuthorization } from "features/data-management/integrations/useIntegrations"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useAppDispatch } from "integrations/redux/hooks"
import { ButtonPrimary, ButtonStatus } from "@nova/component-btn"
import { Select } from "@nova/component-select"

interface TAKIntegrationUIProps {
    authorization: IntegrationAuthorizationResponse<AuthorizationType, IntegrationType.tak>
}
const userContentOption: TAKMission = { name: "Server" }

const TAKIntegrationUI: FunctionComponent<TAKIntegrationUIProps> = ({ authorization }) => {
    const { missions = [], isLoading, error } = useTAKMissions(authorization.workspaceId)
    const [selected, setSelected] = useState<TAKMission>()
    const { trigger, isMutating } = useUpdateIntegrationAuthorization()
    const dispatch = useAppDispatch()

    const isUnchanged =
        (!authorization.settings.mission?.name && selected?.name === userContentOption.name) ||
        (!authorization.settings.mission?.name && !selected?.name) ||
        authorization.settings.mission?.name === selected?.name ||
        !selected

    const initialValue = authorization.settings.mission?.name
        ? missions.find((mission) => mission.name === authorization.settings.mission?.name)
        : userContentOption

    if (error) {
        dispatch(
            setGlobalNotificationContext({
                message: `Failed to retrieve mission list from TAK. Please try again later.`,
                status: GlobalNotificationStatus.error,
                timeoutInMs: 3000,
            })
        )
        console.error(error)
        return <></>
    }

    const handleUpdateClick = async () => {
        const mission =
            !selected?.name || selected.name === userContentOption.name ? null : selected

        await trigger({
            workspaceId: authorization.workspaceId,
            id: authorization.id,
            authUserId: authorization.userId,
            fields: {
                settings: {
                    mission,
                },
            },
        }).then(() =>
            dispatch(
                setGlobalNotificationContext({
                    message: "Your destination has been updated.",
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 3000,
                })
            )
        )
    }

    return isLoading ? (
        <Spinner />
    ) : (
        <div>
            <h5 className="text-gray-100">Destination</h5>
            <div className="flex gap-2">
                <TAKMissionSelect
                    options={[...missions, userContentOption]}
                    onChange={(v) => setSelected(v)}
                    value={selected}
                    defaultValue={initialValue}
                />
                <ButtonPrimary
                    onClick={handleUpdateClick}
                    status={isMutating ? ButtonStatus.Loading : ButtonStatus.Normal}
                    title="Update"
                    disabled={isUnchanged}
                    height="h-fill"
                />
            </div>
        </div>
    )
}

interface TAKMissionSelectProps {
    value: TAKMission | undefined
    options: TAKMission[]
    onChange: (proj: TAKMission | undefined) => void
    defaultValue: TAKMission | undefined
}

const TAKMissionSelect: FunctionComponent<TAKMissionSelectProps> = ({
    value,
    options,
    onChange,
    defaultValue,
}) => {
    return (
        <Select<TAKMission | undefined>
            onChange={onChange}
            options={options.map((project) => ({
                label: project.name,
                value: project,
            }))}
            value={value}
            defaultValue={defaultValue}
            getOptionValue={(o) => o?.name}
            placeholder="Server"
        />
    )
}

export { TAKIntegrationUI }
